import Card from '../../components/common/Card';
import CopyLinkButton from '../../components/common/CopyLinkButton';
import { donationMethodOf, donationMethodLabel } from '../../utils/constants';

/* The overview the Links page opens on — the approved hub design: a row of
   quick-action tiles (the dashboard's .quick-action), then one summary card per
   section, each ending in a "View All →" that opens that section's full list.
   Purely presentational; LinksPage owns the data and the view switch. */

const QUICK_ACTIONS = [
  { key: 'reminder', icon: '/assets/icons/calendar.svg', title: 'Add Reminder', sub: 'Text, no link needed' },
  { key: 'signup', icon: '/assets/icons/signup.svg', title: 'Create Sign-Up', sub: 'Get volunteers' },
  { key: 'link', icon: '/assets/icons/link-tile.svg', title: 'Add Link', sub: 'Photo album, form, etc.' },
  { key: 'donation', icon: '/assets/icons/donation.svg', title: 'Add Donation Link', sub: 'Zelle, Venmo, Apple Pay' },
];

/* "A, B, +3 more" — the first few names, then how many the card isn't
   showing. `total` can exceed names.length when the list is paginated. */
function namesSummary(names, total, max = 2) {
  const shown = names.filter(Boolean).slice(0, max);
  const rest = Math.max(0, (total || names.length) - shown.length);
  return [...shown, rest ? `+${rest} more` : null].filter(Boolean).join(', ');
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/* A Class Basics line, the design's .quad-mini-row: tapping the name opens
   what it's about (`onClick`, titled `openTitle`), the pill copies the link.
   The full URL is on the section's View All page. */
function BasicsRow({ icon, title, url, copyTitle, openTitle, onClick }) {
  return (
    <div className="quad-mini-row">
      <div
        className="mini-row-main"
        style={onClick ? { cursor: 'pointer' } : undefined}
        title={onClick ? openTitle : undefined}
        onClick={onClick}
      >
        <div className="thumb">{icon}</div>
        <div className="info">
          <div className="t">{title}</div>
          <div className="meta">{url ? 'Shareable link' : 'Select a class in the header to get its link'}</div>
        </div>
      </div>
      <CopyLinkButton url={url} className="mini-row-copy" mini title={copyTitle} />
    </div>
  );
}

function HubCard({ title, onView, children }) {
  return (
    <Card title={title}>
      {children}
      <button type="button" className="view-all" onClick={onView}>
        View All →
      </button>
    </Card>
  );
}

export default function LinksHub({
  ctxName,
  consentUrl,
  chatUrl,
  signups,
  signupsTotal,
  signupsLoading,
  links,
  linksTotal,
  linksLoading,
  donationMethods,
  donationMethodsLoading,
  reminders,
  remindersTotal,
  remindersLoading,
  onOpen,
  onQuick,
}) {
  const linkList = links || [];
  const linksPending = linksLoading && !links;

  return (
    <>
      <section className="quick-section">
        <div className="quick-actions">
          {QUICK_ACTIONS.map((qa) => (
            <button type="button" className="quick-action" key={qa.key} onClick={() => onQuick(qa.key)}>
              <img src={qa.icon} alt="" />
              <span className="qa-copy">
                <b>{qa.title}</b>
                <small>{qa.sub}</small>
              </span>
              <span className="arrow">›</span>
            </button>
          ))}
        </div>
      </section>

      <div className="links-hub">
        <HubCard title="Class Basics" onView={() => onOpen('basics')}>
          <BasicsRow
            icon="📋"
            title="Consent Form"
            url={consentUrl}
            copyTitle="Copy consent form link"
            openTitle="View parents who consented"
            onClick={() => onOpen('consent')}
          />
          <BasicsRow
            icon="💬"
            title={ctxName}
            url={chatUrl}
            copyTitle="Copy class group chat link"
            openTitle="Open the page parents see"
            onClick={chatUrl ? () => window.open(chatUrl, '_blank', 'noopener') : undefined}
          />
        </HubCard>

        <HubCard title="💳 Donations" onView={() => onOpen('donations')}>
          <div className="hub-count">
            {donationMethodsLoading
              ? 'Loading…'
              : donationMethods.length
                ? plural(donationMethods.length, 'method')
                : 'No methods yet'}
          </div>
          {donationMethods.length > 0 && (
            <div className="pay-badges">
              {donationMethods.map((d) => {
                const label = donationMethodLabel(d);
                return (
                  <img
                    key={d.id}
                    src={donationMethodOf(d.method).icon}
                    alt={label}
                    title={`${label} · ${d.handle}`}
                  />
                );
              })}
            </div>
          )}
        </HubCard>

        <HubCard title="📝 Sign-Ups" onView={() => onOpen('signups')}>
          <div className="hub-count">
            {signupsLoading ? 'Loading…' : signupsTotal ? plural(signupsTotal, 'sign-up') : 'No sign-ups yet'}
          </div>
          <div className="hub-sub">
            {signupsLoading
              ? ''
              : signupsTotal
                ? namesSummary(signups.map((s) => s.title), signupsTotal)
                : 'Get volunteers for a field trip, book reading or party.'}
          </div>
        </HubCard>

        <HubCard title="🔗 Links" onView={() => onOpen('links')}>
          <div className="hub-count">
            {linksPending ? 'Loading…' : linksTotal ? plural(linksTotal, 'link') : 'No links yet'}
          </div>
          <div className="hub-sub">
            {linksPending
              ? ''
              : linksTotal
                ? namesSummary(linkList.map((l) => l.label), linksTotal)
                : 'Photo albums, forms — anything parents should have handy.'}
          </div>
        </HubCard>

        <HubCard title="📌 Reminders" onView={() => onOpen('reminders')}>
          <div className="hub-count">
            {remindersLoading
              ? 'Loading…'
              : remindersTotal
                ? plural(remindersTotal, 'reminder')
                : 'No reminders yet'}
          </div>
          {!remindersLoading && reminders.length > 0 && (
            <div className="hub-sub">{namesSummary(reminders.map((r) => r.text), remindersTotal)}</div>
          )}
        </HubCard>
      </div>
    </>
  );
}
